import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { AuthCard } from '../components/AuthCard/AuthCard';
import { ResetPasswordForm } from '../components/ResetPasswordForm/ResetPasswordForm';
import { useAuth } from '../hooks/useAuth';
import { authApi } from '../authApi';
import { AUTH_ROUTES } from '../authConstants';

const I18N = 'auth.resetPassword';

// AuthCard forces `text.body` on every Typography; muted copy needs an
// equal-specificity override (same convention as the forms).
const MUTED = { '&.MuiTypography-root': { color: 'text.label' } } as const;

type Status = 'validating' | 'valid' | 'invalid';

/**
 * Landing page for the emailed reset link (`/reset-password?token=…&email=…`).
 * Reads the token + email from the URL, silently validates them, then shows either
 * the reset form (valid) or an "expired link" error (invalid/missing/expired).
 */
export function ResetPasswordPage() {
  const { t } = useTranslation('common');
  const { isAuthenticated, isInitialized, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // The emailed link carries only `?token=…` (the backend keys everything on it).
  const token = searchParams.get('token') ?? '';

  // Missing token can't be valid — start 'invalid' without a round-trip; otherwise
  // start 'validating' and let the effect resolve it (lazy init keeps setState out of
  // the effect body, only inside the async callbacks).
  const [status, setStatus] = useState<Status>(() => (token ? 'validating' : 'invalid'));

  // Signed in and no link: nothing to do here. Signed in WITH a link: the link wins
  // (HOMCO-1393) — an admin reset or activation link must stay usable in a browser
  // where some session survives (access tokens outlive a reset, HOMCO-935). Close
  // that session locally, without leaving the page, then show the form.
  useEffect(() => {
    if (!isInitialized || !isAuthenticated) return;
    if (token) {
      logout({ redirect: false });
    } else {
      navigate(AUTH_ROUTES.HOME, { replace: true });
    }
  }, [isAuthenticated, isInitialized, logout, navigate, token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    authApi
      .validateResetToken(token)
      .then(({ valid }) => {
        if (!cancelled) setStatus(valid ? 'valid' : 'invalid');
      })
      .catch(() => {
        if (!cancelled) setStatus('invalid');
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <AuthCard>
      {status === 'validating' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2" sx={MUTED}>
            {t(`${I18N}.validating`)}
          </Typography>
        </Box>
      )}

      {status === 'invalid' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Box
            sx={theme => ({
              width: theme.avatar.lg,
              height: theme.avatar.lg,
              borderRadius: theme.borderRadius.pill,
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.error.main, 0.14),
              color: 'error.main',
            })}
          >
            <ErrorOutlineIcon />
          </Box>

          <Typography variant="h5" component="h1" sx={{ fontWeight: 'fontWeightMedium', mb: 1 }}>
            {t(`${I18N}.invalidTitle`)}
          </Typography>
          <Typography variant="body2" sx={MUTED}>
            {t(`${I18N}.invalidMessage`)}
          </Typography>

          <Button
            component={RouterLink}
            to={AUTH_ROUTES.FORGOT_PASSWORD}
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 3, mb: 2 }}
          >
            {t(`${I18N}.requestNew`)}
          </Button>

          <Link
            component={RouterLink}
            to={AUTH_ROUTES.LOGIN}
            underline="hover"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              fontWeight: 'fontWeightMedium',
            }}
          >
            <ArrowBackIcon fontSize="small" />
            {t(`${I18N}.backToLogin`)}
          </Link>
        </Box>
      )}

      {status === 'valid' && <ResetPasswordForm token={token} />}
    </AuthCard>
  );
}
